/* =========================================================================
 * SEARCHING FOR A CHAMPIONSHIP, WHICH IS NOT THE SAME AS FILTERING A BOARD.
 *
 * Brian: "The lens did not make anything searchable. In governing bodies, if I
 * want to find every football championship within a governing body or within
 * our entire universe, I should be able to do so."
 *
 * ⚠️ THE ONLY SEARCH BOX ON THAT PAGE SEARCHED BODY NAMES. Typing "football"
 * into it asked "is there an association called football?", got no, and
 * returned nothing — while 833 football championships sat in the object. The
 * lens shipped a curated view over the same records and still did not answer
 * the question, because a preset is something somebody chose in advance and a
 * search is something you ask.
 *
 * So free text is the primitive here, and the activity presets are shortcuts
 * into it rather than a replacement for it.
 *
 * ⚠️ AND IT SEARCHES THE NAME AND THE SPORT TOGETHER, NEVER THE SPORT ALONE.
 *
 * Measured live, 2026-09-21, on 15,061 records:
 *   sport contains "football"  782
 *   name  contains "football"  808
 *   either                     833   ← the true answer
 *
 * 51 of those 833 have a BLANK sport and are reachable by name only:
 * "OHSAA Football Division 7 State Championship", "CIF State Football Division
 * 1-A Championship", "FHSAA Football 1A State Championship". A sport-only
 * search loses all 51 and reports 782 with total confidence. That is the
 * undercount this whole module exists to prevent, and it is invisible, because
 * 782 looks like a perfectly reasonable number.
 *
 * The sport itself is fragmented six ways — Football 679, Flag Football 95,
 * "Football — Independent League" 2, "Football — Florida Invitational
 * Tournament" 2, 8-Player Football 2, Football Bowl Series 2 — so the query
 * also runs against the REPAIRED spelling and the CANONICAL activity from
 * lib/activities.js. That is what lets "cheer" find a record whose sport reads
 * "Spirit — Pom", which contains neither the word cheer nor the word dance.
 * ========================================================================= */

import { activityOf } from './activities.js'
import { isDefectiveName } from './bodyChampionships.js'

const S = (v) => String(v ?? '').trim()
const L = (v) => S(v).toLowerCase()

/**
 * Everything about a record a person might reasonably type. Built once per
 * record per query rather than per term, because the universe is 15k rows and
 * this runs on every keystroke.
 */
function haystack(c) {
  const r = c?.raw || c || {}
  const sp = activityOf(c)
  return L([
    S(c?.name ?? r.championship_name),
    S(r.sr_conference ?? c?.conference),
    S(r.sr_state_location ?? c?.sr_state_location),
    S(r.sr_level),
    S(r.sr_segment),
    sp.raw,
    /* the repaired spelling, so "Cheer — Game Day" is findable however it was
       stored, and the canonical activity, so a sport that never spells the
       word out is still reachable by it */
    sp.clean,
    sp.activity,
    S(c?.champion ?? r.current_champion),
  ].join('  '))
}

/**
 * ⚠️ EVERY TERM MUST MATCH, NOT ANY TERM.
 * "ohio football" means both, and an OR would answer it with every football
 * title in the country plus everything in Ohio — a longer query returning a
 * bigger result is a search that punishes you for being specific.
 */
export function searchChampionships(universe = [], query = '', { matcher = null } = {}) {
  const terms = L(query).split(/\s+/).filter(Boolean)
  const scoped = matcher ? universe.filter(matcher) : universe

  if (!terms.length) return summarise(scoped, terms)

  const rows = scoped.filter((c) => {
    const hay = haystack(c)
    for (const t of terms) if (!hay.includes(t)) return false
    return true
  })
  return summarise(rows, terms)
}

/**
 * ⚠️ FLAG FOOTBALL IS NOT FOOTBALL, AND THE RESULT HAS TO SAY SO.
 *
 * They are different titles bought by different buyers, and 95 flag-football
 * records will always be dragged in by a search for "football" because the
 * word is right there in the name. Returning them is correct; returning them
 * silently folded into a single number labelled "football" is not — it would
 * report 782 football championships to a rep quoting a count, 95 of which are
 * a different product.
 *
 * So the breakdown by activity travels WITH the total, always, and the UI
 * renders it beside the number rather than behind a disclosure.
 */
function summarise(rows, terms) {
  const byActivity = new Map()
  let flagged = 0
  let defective = 0
  for (const c of rows) {
    const a = activityOf(c).activity
    byActivity.set(a, (byActivity.get(a) || 0) + 1)
    if (isDefectiveName(c)) defective += 1
  }
  return {
    rows,
    terms,
    total: rows.length,
    defective,
    flagged,
    byActivity: [...byActivity].sort((a, b) => b[1] - a[1]).map(([activity, count]) => ({ activity, count })),
  }
}

/**
 * The two scopes, named. Both are required — Brian asked for "within a
 * governing body OR within our entire universe" and they are genuinely
 * different questions: one is "what does GHSA crown", the other is "where does
 * football live".
 */
export const SCOPES = [
  { key: 'universe', label: 'Everything we hold' },
  { key: 'body', label: 'This governing body' },
]
